import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Save the event so it can be triggered later
      setDeferredPrompt(e);
      // Show our custom install prompt
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`User response: ${outcome}`);

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Hide for 7 days
    localStorage.setItem('installPromptDismissed', Date.now() + 7 * 24 * 60 * 60 * 1000);
  };

  // Check if prompt was recently dismissed
  useEffect(() => {
    const dismissedUntil = localStorage.getItem('installPromptDismissed');
    if (dismissedUntil && Date.now() < parseInt(dismissedUntil)) {
      setShowPrompt(false);
    }
  }, []);

  if (!showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <div className="install-prompt">
      <div className="install-prompt-content">
        <div className="install-prompt-icon">
          <Download size={24} />
        </div>
        <div className="install-prompt-text">
          <h4>Install Baad.tv</h4>
          <p>Install our app for a better experience!</p>
        </div>
        <div className="install-prompt-actions">
          <Button onClick={handleInstall} size="sm">
            Install
          </Button>
          <button onClick={handleDismiss} className="dismiss-btn">
            <X size={20} />
          </button>
        </div>
      </div>

      <style jsx>{`
        .install-prompt {
          position: fixed;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1000;
          width: 90%;
          max-width: 500px;
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }

        .install-prompt-content {
          background: #1a1a1a;
          border: 2px solid #E62F88;
          border-radius: 12px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
        }

        .install-prompt-icon {
          color: #E62F88;
          flex-shrink: 0;
        }

        .install-prompt-text {
          flex: 1;
        }

        .install-prompt-text h4 {
          color: #ffffff;
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 0.25rem 0;
        }

        .install-prompt-text p {
          color: #b0b0b0;
          font-size: 0.875rem;
          margin: 0;
        }

        .install-prompt-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .dismiss-btn {
          background: transparent;
          border: none;
          color: #b0b0b0;
          cursor: pointer;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .dismiss-btn:hover {
          background: #2a2a2a;
          color: #ffffff;
        }

        @media (max-width: 768px) {
          .install-prompt {
            bottom: 20px;
            width: calc(100% - 2rem);
          }

          .install-prompt-content {
            padding: 0.875rem;
          }

          .install-prompt-text h4 {
            font-size: 0.9rem;
          }

          .install-prompt-text p {
            font-size: 0.8rem;
          }
        }
      `}</style>
    </div>
  );
};

export default InstallPrompt;
